import React, { useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { Button, Col, DatePicker, Form, Input, Modal, Popconfirm, Row, Select } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { isAuthenticated } from "../../../commons/Auth/auth";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Error, Success } from "../../../commons/Messages/Messages";
import { createBooking, deleteBooking, updateBooking } from "./api";

const DATE_TIME_FORMAT = "DD.MM.YYYY HH:mm";

/**
 * Shows why a booking could not be saved. A 409 carries the clashing booking, so the message
 * can name it in the user's language and time zone.
 */
export const showBookingError = (err, t) => {
  const conflict = err?.response?.status === 409 && err.response.data?.conflict;
  if (conflict) {
    Error(
      t("{{room}} is already booked from {{start}} to {{end}} ({{title}}).", {
        room: conflict.room?.name || t("This room"),
        start: dayjs(conflict.start).format(DATE_TIME_FORMAT),
        end: dayjs(conflict.end).format(DATE_TIME_FORMAT),
        title: conflict.title
      })
    );
    return;
  }
  generalErrorAlert(err);
};

const employeeLabel = e => [e?.firstName, e?.lastName].filter(Boolean).join(" ") || e?.email;

/**
 * Add or edit a room booking. `booking` is the booking to edit (null for a new one);
 * `defaults` may preset `room` and `start` for a new one (e.g. from a click on the timeline).
 */
const ModalAddRoom = ({
  open,
  booking,
  defaults,
  rooms,
  onClose,
  onSaved,
  canUpdate,
  canDelete
}) => {
  const { t } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [employees, setEmployees] = useState([]);
  const isEdit = Boolean(booking?._id);
  const readOnly = isEdit && !canUpdate;

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      form.setFieldsValue({
        room: booking.room?._id || booking.room,
        title: booking.title,
        start: dayjs(booking.start),
        end: dayjs(booking.end),
        employee: booking.employee?._id || booking.employee || undefined,
        notes: booking.notes
      });
    } else {
      const start = defaults?.start
        ? dayjs(defaults.start)
        : dayjs().add(1, "hour").startOf("hour");
      form.setFieldsValue({
        room: defaults?.room || rooms.find(r => r.active)?._id,
        title: "",
        start,
        end: start.add(1, "hour"),
        employee: undefined,
        notes: ""
      });
    }
  }, [open, booking, defaults]);

  // The employee is optional, so a failed list only leaves the select empty.
  useEffect(() => {
    if (!open || employees.length) return;
    const providerId = isAuthenticated()?.serviceProviderId;
    if (!providerId) return;
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/employees/employee/creator/${providerId}`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") }
      })
      .then(res => setEmployees(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, [open]);

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const body = {
      room: values.room,
      title: values.title.trim(),
      start: values.start.toISOString(),
      end: values.end.toISOString(),
      employee: values.employee || null,
      notes: values.notes || ""
    };
    setSaving(true);
    try {
      if (isEdit) await updateBooking(booking._id, body);
      else await createBooking(body);
      Success(isEdit ? t("Booking updated successfully") : t("Booking added successfully"));
      onSaved();
    } catch (err) {
      showBookingError(err, t);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteBooking(booking._id);
      Success(t("Booking deleted successfully"));
      onSaved();
    } catch (err) {
      generalErrorAlert(err);
    } finally {
      setDeleting(false);
    }
  };

  const roomOptions = rooms
    .filter(r => r.active || r._id === (booking?.room?._id || booking?.room))
    .map(r => ({
      value: r._id,
      label: r.active ? r.name : `${r.name} (${t("Inactive")})`,
      disabled: !r.active
    }));

  // The booking's employee stays readable even if the list could not be loaded.
  const employeeOptions = employees.map(e => ({ value: e._id, label: employeeLabel(e) }));
  if (booking?.employee?._id && !employeeOptions.some(o => o.value === booking.employee._id)) {
    employeeOptions.push({ value: booking.employee._id, label: employeeLabel(booking.employee) });
  }

  const footer = [
    isEdit && canDelete && (
      <Popconfirm
        key="delete"
        title={t("Delete this booking?")}
        description={t("This action cannot be undone.")}
        okText={t("Delete")}
        cancelText={t("Cancel")}
        okButtonProps={{ danger: true }}
        onConfirm={handleDelete}
      >
        <Button danger icon={<DeleteOutlined />} loading={deleting}>
          {t("Delete")}
        </Button>
      </Popconfirm>
    ),
    <Button key="cancel" onClick={onClose}>
      {readOnly ? t("Close") : t("Cancel")}
    </Button>,
    !readOnly && (
      <Button key="save" type="primary" loading={saving} onClick={handleSave}>
        {t("Save")}
      </Button>
    )
  ].filter(Boolean);

  return (
    <Modal
      centered
      width={640}
      open={open}
      forceRender
      title={isEdit ? t("Edit booking") : t("Add booking")}
      onCancel={onClose}
      footer={footer}
    >
      <Form form={form} layout="vertical" requiredMark={false} disabled={readOnly}>
        <Row gutter={[16, 0]}>
          <Col xs={24} md={12}>
            <Form.Item
              name="room"
              label={t("Room")}
              rules={[{ required: true, message: t("Please select a room") }]}
            >
              <Select
                options={roomOptions}
                placeholder={t("Select a room")}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="title"
              label={t("Title")}
              rules={[
                { required: true, whitespace: true, message: t("Please enter a title") },
                { max: 200 }
              ]}
            >
              <Input placeholder={t("e.g. Team meeting")} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="start"
              label={t("Start")}
              rules={[{ required: true, message: t("Please select the start") }]}
            >
              <DatePicker
                className="full-width"
                style={{ width: "100%" }}
                showTime={{ format: "HH:mm", minuteStep: 5 }}
                format={DATE_TIME_FORMAT}
                onChange={value => {
                  // Keep the duration when the start moves past the end.
                  const end = form.getFieldValue("end");
                  if (value && (!end || !end.isAfter(value))) {
                    form.setFieldsValue({ end: value.add(1, "hour") });
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="end"
              label={t("End")}
              dependencies={["start"]}
              rules={[
                { required: true, message: t("Please select the end") },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const start = getFieldValue("start");
                    if (!value || !start || value.isAfter(start)) return Promise.resolve();
                    return Promise.reject(new window.Error(t("End must be after start")));
                  }
                })
              ]}
            >
              <DatePicker
                className="full-width"
                style={{ width: "100%" }}
                showTime={{ format: "HH:mm", minuteStep: 5 }}
                format={DATE_TIME_FORMAT}
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item name="employee" label={t("Booked for (optional)")}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={t("Select an employee")}
                options={employeeOptions}
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item name="notes" label={t("Notes")}>
              <Input.TextArea rows={3} maxLength={2000} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ModalAddRoom;
